import { isLayerGroupId, type DatasetCatalogItem } from "./datasetCatalogTypes";
import { osmGroupLoaderSnippet } from "./datasetLoaderSnippets";

/**
 * What a dataset's copy control hands over, decided in one place.
 *
 * #206 asks for "a way to copy the path". The path is the wrong thing to give
 * out, and giving it out is why the request exists: an absolute path is
 * specific to one machine, one user and one mount, so pasting it into a node
 * produces code that works until someone else opens the dataflow. The portable
 * reference is ``curio_data_path("<id>")`` — the sandbox resolves it to a
 * real location at execution time, and it is exactly what the palette's own
 * generated loaders emit.
 *
 * So the copy control hands over the reference, and the details view shows the
 * path as information. One function decides, so no surface has to reason about
 * ids versus dirNames versus paths again.
 */

/**
 * Ids are interpolated into Python source, so only ids matching this whitelist
 * can appear inside a ``curio_load_data("<id>")`` call — one with a quote or
 * a backslash would break out of the string literal. Kept in step with
 * ``SAFE_DATASET_ID_RE`` in ``datasetLoaderSnippets.ts`` and the backend's
 * ``_SAFE_DATASET_ID_RE``.
 */
const SAFE_DATASET_ID_RE = /^[A-Za-z0-9][A-Za-z0-9._@-]{0,199}$/;

export interface DatasetReference {
  /** The Python to paste into a node: one call, or a layer group's loader. */
  code: string;
  /** Where the bytes actually are, for display. Empty when unknown. */
  location: string;
}

/** What a reference is built from: a catalog item, or the fields it needs. */
export type DatasetReferenceSource = Pick<DatasetCatalogItem, "id" | "path" | "uri"> &
  Partial<Pick<DatasetCatalogItem, "format" | "groupLayers">>;

/**
 * The reference and the location for *dataset*.
 *
 * Falls back to a quoted literal path when the id cannot be embedded safely —
 * the same fallback ``pathExpr`` makes when generating a loader, so what is
 * copied always matches what the palette would have written.
 */
export function datasetReference(dataset: DatasetReferenceSource): DatasetReference {
  const location = String(dataset.path || dataset.uri || "");
  const id = String(dataset.id ?? "");
  // A layer group's id names no file, so no one call reads it. What goes into
  // a node is the group's loader, which reads each layer: the one a group card
  // dropped on the canvas loads its layers with (`createLayerGroupDragPayload`).
  if (isLayerGroupId(id) && dataset.groupLayers && dataset.groupLayers.length > 0) {
    const snippet = osmGroupLoaderSnippet(dataset.groupLayers);
    return { code: [...snippet.imports, snippet.code].join("\n"), location };
  }
  // A collection is read with `curio_load_collection`, which adds a readable
  // path for each of its files; its data file alone is only the index.
  const call = dataset.format === "collection" ? "curio_load_collection" : "curio_load_data";
  const code = SAFE_DATASET_ID_RE.test(id)
    ? `${call}(${JSON.stringify(id)})`
    : JSON.stringify(location);
  return { code, location };
}

/** Just the string a copy control puts on the clipboard. */
export function datasetReferenceCode(dataset: DatasetReferenceSource): string {
  return datasetReference(dataset).code;
}
